import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CoreDirectivesModule } from '@core/directives/directives';
import { TranslocoService } from '@jsverse/transloco';
import { NgbModal, NgbTooltip, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';
import { Actions, ofType } from '@ngrx/effects';
import { FlatpickrModule } from 'angularx-flatpickr';
import { GlobalService } from 'app/global/global.service';
import { SelectFieldComponent } from 'app/main/pages/contract-control/editable-fields/select-field/select-field.component';
import { TextFieldComponent } from 'app/main/pages/contract-control/editable-fields/text-field/text-field.component';
import { IGroup } from 'app/main/pages/settings/documenttype/document-type-settings-modal/document-type-settings.dto';
import { ExtractSenderDetailPipe } from 'app/pipes/documents-right-sidebar-pipes/extract-sender-details.pipe';
import { MomentPipe } from 'app/pipes/moment.pipe';
import { UserTimeZoneFormatPipe } from 'app/pipes/user-timezone-format.pipe';
import { TranslocoRootModule } from 'app/transloco-root.module';
import { merge, Subject, take, takeUntil } from 'rxjs';
import { IDocumentDetailRootDTO, Tag, VisibilityEnum } from '../../../dto/document-details.dto';
import * as DocumentListingActions from '../../../store/document-listing.actions';
import { AddToContractControlModalComponent } from '../../add-to-contract-control-modal/add-to-contract-control-modal.component';
import { DocumentHelperFacade } from '../../document-helper.facade';
import { DynamicFieldComponent, FieldActionEvent } from '../dynamic-field/dynamic-field.component';

export interface DetailsSectionUpdateEvent {
  action: 'dueDateChanged';
  data: string;
}

@Component({
  selector: 'app-details-section',
  standalone: true,
  imports: [
    CommonModule,
    CoreDirectivesModule,
    TranslocoRootModule,
    NgSelectModule,
    TextFieldComponent,
    SelectFieldComponent,
    NgbTooltipModule,
    ExtractSenderDetailPipe,
    FlatpickrModule,
    MomentPipe,
    UserTimeZoneFormatPipe,
    FormsModule,
    ReactiveFormsModule,
    DynamicFieldComponent,
  ],
  templateUrl: './details-section.component.html',
  styleUrl: './details-section.component.scss'
})
export class DetailsSectionComponent implements OnInit, OnChanges {

  @Output() updateDocument = new EventEmitter<void | DetailsSectionUpdateEvent>();
  @ViewChild('tooltip') tooltip: NgbTooltip;

  @Input() group: IGroup;
  @Input() formGroup: import('@angular/forms').FormGroup;
  @Input() isDueDateDisabled: boolean;
  @Input() shouldShowDueDateField: boolean = false;
  @Input() disableFlatpickr: ((date: Date) => boolean)[];
  @Input() hasFullAccessToDocument: boolean;
  @Input() canEditDocument: boolean;
  @Input() documentData: IDocumentDetailRootDTO;
  @Input() sectionName: string = 'send-document';

  public tags: Tag[] = [];
  private destroy$ = new Subject<void>();
  public VisibilityEnum = VisibilityEnum;

  constructor(
    private _modalService: NgbModal,
    private _actions$: Actions,
    private _globalService: GlobalService,
    private _translocoService: TranslocoService,
    private _documentHelperFacade: DocumentHelperFacade
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['group'] && !changes['group'].firstChange) {
      this.initializeFormControls();
    }
  }

  ngOnInit(): void {
    this.initializeFormControls();
  }


  /**
   * Initializes the form controls.
   * 
   * This method initializes the form controls for the dynamic fields.
   */
  private initializeFormControls(): void {
    if (this.group && this.formGroup) {
      for (const field of this.group.fields) {
        let initialValue = field?.value;
        const dateFields = ['signing_deadline', 'signing_reminder'];
        if (dateFields.includes(field.field_name) && (initialValue === 0 || initialValue === '0')) {
          initialValue = null;
        }

        if(field.field_name === 'tags') {
          initialValue = field?.value ?? [];
        }
        
        if (this.formGroup.contains(field.field_name)) {
          const control = this.formGroup.get(field.field_name);
          // Avoid emitting valueChanges during initialization; it can trigger heavy side-effects
          // (e.g. API calls / store updates) and cause UI stalls if repeated.
          if (control?.value !== initialValue) {
            control?.setValue(initialValue, { emitEvent: false });
          }
        } else {
          const newControl = new FormControl(initialValue);
          this.formGroup.addControl(field.field_name, newControl);
        }
      }
    }
  }



  /**
   * Handles field actions.
   * 
   * This method handles the field actions emitted by the dynamic field component.
   * 
   * @param event The field action event.
   */
  handleFieldAction(event: FieldActionEvent): void {
    switch(event.action) {
      case 'updateVisibility':
        this.updateDocumentVisibility();
        break;
      case 'openContractControl':
        this.openAddToContractControlModalComponent();
        break;
      case 'navigateToContract':
        this.navigateToLinkedContract(event.data);
        break;
      case 'updateDueDate':
        this.updateDocumentDueDate(event.data);
        break;
      case 'addTag':
        this.onAdd(event.data);
        break;
      case 'removeTag':
        this.removeTag(event.data);
        break;
    }
  }



  /**
   * Removes a tag from the document.
   * 
   * This method takes the tag to remove from the input element and removes it from the
   * `tags` form control. It then emits the `updateDocument` event to notify
   * the parent component to update the document.
   * 
   * @param tagToRemove The tag to remove from the document.
   */
  removeTag(tagToRemove: Tag): void {
    this.formGroup.get('tags')?.setValue(
      this.formGroup.get('tags')?.value.filter((tag: Tag) => tag.value !== tagToRemove.value)
    );
    this.formGroup.get('tags').updateValueAndValidity();
    this.formGroup.get('tags').markAsTouched();
    this.updateDocument.emit();
  }


  /**
   * Adds a new tag to the document.
   * 
   * This method takes the new tag from the input element and adds it to the
   * `tags` form control. It then emits the `updateDocument` event to notify
   * the parent component to update the document.
   * 
   * @param newTag The new tag to add to the document.
   */
  onAdd(newTag: Tag): void {
    const currentTags: Tag[] = this.formGroup.get('tags')?.value || [];
    this.formGroup.get('tags')?.setValue([...currentTags, newTag]);
    this.updateDocument.emit();
  }


  /**
   * Updates the due date of the document.
   * 
   * This method takes the due date from the input element and sets it to the
   * `due_date` form control. It then emits the `updateDocument` event to notify
   * the parent component to update the document.
   * 
   * @param $event The event object containing the due date.
   */
  updateDocumentDueDate($event: Event): void {
    const inputElement = $event.target as HTMLInputElement;
    const dueDate = inputElement?.value ?? '';
    this.formGroup.get('due_date')?.setValue(dueDate);
    this.updateDocument.emit({ action: 'dueDateChanged', data: dueDate });
  }



  /**
     * Opens the AddToContractControlModalComponent modal.
     * 
     * This method checks if the user has full access to the document by evaluating
     * the `hasFullAccessToDocument$` observable. If the user has full access, it 
     * opens the modal with the specified configuration options:
     * - `size`: Sets the modal size to large ('lg').
     * - `backdrop`: Disables the backdrop.
     * - `windowClass`: Applies the 'add-to-contract-control-modal' CSS class to the modal window.
     */
  openAddToContractControlModalComponent(): void {
    if (this.hasFullAccessToDocument) {
      const instance = this._modalService.open(AddToContractControlModalComponent, {
        size: 'lg',
        backdrop: false,
        windowClass: 'add-to-contract-control-modal'
      });
      if (instance) {
        this._actions$
          .pipe(
            ofType(DocumentListingActions.connectContractSuccess),
            take(1),
            takeUntil(merge(this.destroy$, instance.closed))
          )
          .subscribe(
            {
              next: () => {
                instance.close();
                this._globalService.toastrSuccess(this._translocoService.translate('Contact has been linked (Contract control section - contact box) - Contact has been linked'));
              }
            }
          )
      }
    }
  }


  /**
  * Navigate to linked contract
  * @param contractId 
  */
  navigateToLinkedContract(contractId: number): void {
    this._documentHelperFacade.openWindow(`/#/contract-control/view/${contractId}`);
  }





  updateDocumentVisibility(): void {
    let currentVisibility = this.formGroup.get('visibility')?.value;
    if (currentVisibility === VisibilityEnum.Private) {
      currentVisibility = VisibilityEnum.Public;
    } else {
      currentVisibility = VisibilityEnum.Private;
    }
    this.formGroup.get('visibility')?.setValue(currentVisibility);
    this.updateDocument.emit();
  }
}
